import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CARD_H, CARD_W, nhanGheTrong, tinhBoCuc } from "./layout";
import { DeNode, DeStage } from "./types";
import { VO_DICH } from "../shared/ui";

/**
 * Sơ đồ nhánh kép cho cơ thủ: ba khung có nhãn, kéo và chụm hai ngón để xem.
 *
 * VÌ SAO KHÔNG PHẢI MẤY CỘT CUỘN NGANG như bản đầu: cuộn ngang cho thấy từng mẩu sơ
 * đồ mà không bao giờ cho thấy HÌNH DẠNG của nó - mà hình dạng chính là thứ phải hiểu
 * ở thể thức này. Cơ thủ cần đọc được "tôi đang ở nhánh thua, thua nữa là về" chỉ bằng
 * cách nhìn, không phải bằng cách cuộn rồi ghép lại trong đầu.
 *
 * TOẠ ĐỘ VÀ ĐƯỜNG NỐI ĐỀU TỪ DỮ LIỆU, không từ đo đạc DOM: ./layout.ts nhận chính các
 * liên kết winToId/loseToId server gửi xuống. Không chỗ nào ở đây tính lại ai đi tiếp.
 *
 * Mở ra là VỪA KHUNG - ở 390px thì sơ đồ 8 người đã rộng gấp ba màn hình, nên bắt đầu
 * bằng một cái nhìn tổng thể rồi để người xem tự phóng vào chỗ mình quan tâm.
 */

const MIN = 0.3;
const MAX = 1.6;

/**
 * Bốn nút công cụ vẽ bằng SVG, không dùng ký tự Unicode.
 *
 * Bản cũ dùng −, ◎, ⤢, +: bốn ký tự ấy đến từ bốn khối Unicode khác nhau, nên trên
 * điện thoại chúng rơi vào các font dự phòng khác nhau, mỗi cái một chiều cao và một
 * đường gốc. Kết quả là nút "vừa khung" lệch hẳn so với ba nút kia - và không có giá
 * trị CSS nào chỉnh được, vì lệch nằm trong chính số đo của font.
 */
const Icon = ({ d, chuY }: { d: string; chuY?: boolean }) => (
  <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" focusable="false"
       fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {chuY ? <circle cx="10" cy="10" r="3" fill="currentColor" stroke="none" /> : null}
    <path d={d} />
  </svg>
);

const ICON_TRU = "M4 10h12";
const ICON_CONG = "M10 4v12M4 10h12";
const ICON_TOI = "M10 2v2M10 16v2M2 10h2M16 10h2M10 4a6 6 0 100 12 6 6 0 000-12";
const ICON_VUA = "M8 3H3v5M17 8V3h-5M12 17h5v-5M3 12v5h5";

/**
 * Giữ mặt phẳng luôn chạm khung nhìn, và trả về PHẦN DƯ của chiều dọc.
 *
 * Hai việc, và việc thứ hai mới là cái quan trọng:
 *
 * 1. Kẹp. Trước đây kéo bao xa cũng được, nên lỡ tay là sơ đồ bay ra ngoài và màn
 *    hình trống trơn - không có gì nói phải kéo ngược về đâu.
 * 2. Phần dư. Khi đã chạm mép trên/mép dưới mà tay vẫn kéo tiếp, phần kéo thừa đó
 *    được trả ra để NỐI SANG TRANG. Đó đúng là cách mọi vùng cuộn lồng nhau vẫn
 *    hành xử: hết đường trong thì đẩy tiếp ra ngoài. Nhờ nó, một ngón vẫn di chuyển
 *    sơ đồ đủ hai chiều như trước, mà người dùng không bao giờ kẹt lại trong sơ đồ -
 *    cứ vuốt tiếp là trang cuộn.
 *
 * Mặt phẳng nhỏ hơn khung thì căn giữa: lúc đó không có đường nào để đi, mọi cú vuốt
 * dọc đều nối thẳng sang trang.
 */
export const kepKhungNhin = (
  v: { s: number; x: number; y: number },
  khung: { width: number; height: number },
  vp: { w: number; h: number },
): { view: { s: number; x: number; y: number }; duDoc: number } => {
  const mot = (gia: number, kich: number, khungNhin: number) =>
    kich <= khungNhin ? (khungNhin - kich) / 2 : Math.min(0, Math.max(khungNhin - kich, gia));

  const y = mot(v.y, khung.height * v.s, vp.h);

  return {
    view: { s: v.s, x: mot(v.x, khung.width * v.s, vp.w), y },
    duDoc: v.y - y,
  };
};

const seatName = (node: DeNode, slot: 1 | 2, nhan: Record<number, string> | undefined) => {
  const player = node.players[slot - 1];
  if (player?.name) {
    return { text: player.name, mo: false };
  }
  /* Ghế chết thì nói thẳng là miễn đấu. Nhãn nguồn ("Thua T1") ở đây là một lời hứa
     sai: T1 là cặp miễn đấu nên không có người thua nào sẽ tới. Cờ do server gửi -
     client KHÔNG tự suy, vì suy được thì phải dựng lại luật đi tiếp lần thứ hai. */
  /* Chỉ ghế KHÔNG CÓ AI mới là miễn đấu, và ghế trống của một cặp miễn đấu KHÔNG phải
     lúc nào cũng là ghế dưới: ô nhánh thua nhận người thua của một cặp miễn đấu có ghế
     trống ở TRÊN, người được đi tiếp ngồi dưới. Nhánh name ở trên đã lo việc đó - tên
     thắng mọi suy đoán theo vị trí ghế. */
  if (player?.byeSeat || node.isBye) {
    return { text: "Miễn đấu", mo: true };
  }

  return { text: nhan?.[slot] ?? "—", mo: true };
};

export default function DeBracket({
  stages,
  myPlayerId,
}: {
  stages: DeStage[];
  myPlayerId: string | null;
}) {
  const vpRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  /* Bản sao của khung nhìn đọc được NGAY, không chờ render.
     Trình duyệt dồn nhiều pointermove vào một nhịp; đọc `view` của closure lúc đó là
     đọc giá trị của nhịp trước, nên mỗi bước kéo lại tính từ một chỗ cũ và sơ đồ giật. */
  const viewRef = useRef(view);
  viewRef.current = view;


  const boCuc = useMemo(() => tinhBoCuc(stages), [stages]);
  const nhan = useMemo(() => nhanGheTrong(stages), [stages]);
  const nodes = useMemo(() => stages.flatMap((s) => s.nodes), [stages]);

  /** Cửa duy nhất để đổi khung nhìn: mọi đường đi qua đây đều được kẹp. */
  const datView = useCallback(
    (muon: { s: number; x: number; y: number }): number => {
      const vp = vpRef.current;
      if (!vp) {
        return 0;
      }
      const { view: kep, duDoc } = kepKhungNhin(muon, boCuc, { w: vp.clientWidth, h: vp.clientHeight });
      viewRef.current = kep;
      setView(kep);

      return duDoc;
    },
    [boCuc]
  );

  /* Ô chung kết: ô duy nhất không có đường thắng đi tiếp. Chỉ người thắng ở đó mới là
     vô địch - gắn cúp theo riêng cờ isWinner thì nửa sơ đồ đầy cúp. */
  const chungKetId = useMemo(() => {
    const ko = nodes.filter((n) => !n.winToId);

    return ko.length === 1 ? ko[0].nodeId : null;
  }, [nodes]);

  const vuaKhung = useCallback(() => {
    const vp = vpRef.current;
    if (!vp || !boCuc.width || !boCuc.height) {
      return;
    }
    const s = Math.max(MIN, Math.min(1, Math.min(vp.clientWidth / boCuc.width, vp.clientHeight / boCuc.height)));
    datView({ s, x: Math.max(0, (vp.clientWidth - boCuc.width * s) / 2), y: 0 });
  }, [boCuc.width, boCuc.height, datView]);

  /**
   * Ô của tôi ở VÒNG XA NHẤT tôi đã tới - không phải ô đầu tiên tìm thấy.
   *
   * Ô của tôi nằm rải khắp sơ đồ: Vòng 1, Vòng 2, rồi nhánh thua... Lấy ô đầu tiên là
   * luôn nhảy về Vòng 1, tức là về trận đã đánh xong từ lâu. Thứ cơ thủ mở sơ đồ ra để
   * tìm là chỗ mình ĐANG đứng.
   */
  const oCuaToi = useMemo(() => {
    const cua = nodes.filter((n) => n.mine);
    if (cua.length === 0) {
      return null;
    }

    return cua.reduce((xa, n) => (n.stage > xa.stage ? n : xa));
  }, [nodes]);

  /**
   * Đưa ô của tôi vào giữa khung, ở mức phóng đọc được.
   *
   * Ở 390px, thu cả sơ đồ cho vừa khung thì chữ còn chừng 4px - đúng nghĩa là không
   * đọc được gì. Mà câu đầu tiên của cơ thủ khi mở sơ đồ ra là "trận của tôi ở đâu",
   * không phải "sơ đồ hình gì".
   */
  const veTranCuaToi = useCallback(() => {
    const vp = vpRef.current;
    const p = oCuaToi ? boCuc.nodes[oCuaToi.nodeId] : null;
    if (!vp || !p) {
      vuaKhung();

      return;
    }
    const s = 1;
    datView({
      s,
      x: vp.clientWidth / 2 - (p.x + CARD_W / 2) * s,
      y: vp.clientHeight / 2 - (p.y + CARD_H / 2) * s,
    });
  }, [boCuc, oCuaToi, vuaKhung, datView]);

  /**
   * CHỈ ĐẶT KHUNG NHÌN MỘT LẦN, lúc mở ra.
   *
   * Trang tự nạp lại 20 giây một lần. Nếu mỗi lần có dữ liệu mới lại kéo khung nhìn về
   * chỗ cũ thì người đang xem một nhánh khác bị giật về chỗ cũ giữa chừng - không hiểu
   * vì sao, và không có cách nào ngăn. Dữ liệu mới cứ vào, còn khung nhìn là của người
   * đang xem. Muốn quay lại thì bấm nút ◎ - một cú bấm cố ý, không phải một cú giật.
   */
  const daDat = useRef(false);
  useEffect(() => {
    if (daDat.current || boCuc.bands.length === 0 || !vpRef.current) {
      return;
    }
    daDat.current = true;
    veTranCuaToi();
  }, [boCuc, veTranCuaToi]);

  /* Kéo bằng một ngón, phóng bằng hai ngón. Giữ bằng Map chứ không bằng hai biến: một
     ngón nhấc lên giữa chừng là chuyện thường, và lúc đó phải quay về chế độ kéo chứ
     không được treo ở chế độ phóng. */
  const ngon = useRef(new Map<number, { x: number; y: number }>());
  const moc = useRef<{ x: number; y: number; d: number; s: number; cx: number; cy: number } | null>(null);

  const xuong = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    ngon.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moc.current = null;
  };

  const di = (e: React.PointerEvent<HTMLDivElement>) => {
    const truoc = ngon.current.get(e.pointerId);
    if (!truoc) {
      return;
    }
    ngon.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const ds = Array.from(ngon.current.values());
    const vp = vpRef.current;
    if (!vp) {
      return;
    }
    const r = vp.getBoundingClientRect();

    if (ds.length === 1) {
      /* Kéo theo ĐỘ DỊCH của từng bước, không theo một cái mốc cố định từ lúc đặt
         tay. Mốc cố định cộng dồn sai số một khi khung nhìn bị kẹp: tay đi tiếp mà
         sơ đồ đã chạm mép thì khoảng cách tới mốc cứ lớn dần, và phần dư trả sang
         trang lớn dần theo - trang cuộn vọt đi.

         Đọc khung nhìn từ viewRef chứ không từ `view` của closure, và KHÔNG đọc ref
         nào bên trong hàm cập nhật của setView: hàm đó chạy ở pha render, lúc ấy
         người dùng có thể đã nhấc tay và ref đã bị xoá - đúng lỗi "Cannot read
         properties of null (reading 'x')" từng làm sập cả trang. */
      /* KẸP vị trí hiện tại TRƯỚC khi cộng độ dịch. Khung nhìn đã lưu có thể nằm
         ngoài giới hạn của khung hiện tại - đổi hướng màn hình, hoặc khung đo được 0
         lúc mới dựng. Cộng thẳng vào giá trị cũ thì nguyên khoảng chênh lệch đó rơi
         hết vào phần dư, và cú kéo đầu tiên làm trang cuộn vọt đi mấy nghìn pixel.
         Chênh lệch ấy là việc của sơ đồ tự sửa, không phải của trang. */
      const v = kepKhungNhin(viewRef.current, boCuc, { w: vp.clientWidth, h: vp.clientHeight }).view;
      const duDoc = datView({
        s: v.s,
        x: v.x + (e.clientX - truoc.x),
        y: v.y + (e.clientY - truoc.y),
      });

      /* Hết đường đi trong sơ đồ thì đẩy tiếp ra trang. Không có bước này,
         touch-action: none biến sơ đồ thành một cái bẫy: vuốt dọc mà trang đứng yên,
         và người dùng không biết phải làm gì. */
      if (duDoc) {
        window.scrollBy(0, -duDoc);
      }

      return;
    }

    const d = Math.hypot(ds[0].x - ds[1].x, ds[0].y - ds[1].y);
    const cx = (ds[0].x + ds[1].x) / 2 - r.left;
    const cy = (ds[0].y + ds[1].y) / 2 - r.top;
    if (!moc.current || !moc.current.d) {
      moc.current = { x: 0, y: 0, d, s: view.s, cx: view.x, cy: view.y };

      return;
    }
    // Cùng lý do như nhánh một ngón: không đọc ref ở chỗ có thể chạy trễ.
    const m = moc.current;
    const s = Math.max(MIN, Math.min(MAX, (m.s * d) / m.d));
    const k = s / m.s;
    // Giữ nguyên điểm giữa hai ngón: không có bước này thì mỗi lần chụm là sơ đồ nhảy.
    datView({ s, x: cx - (cx - m.cx) * k, y: cy - (cy - m.cy) * k });
  };

  const len = (e: React.PointerEvent<HTMLDivElement>) => {
    ngon.current.delete(e.pointerId);
    moc.current = null;
  };

  const phong = (buoc: number) => {
    const vp = vpRef.current;
    if (!vp) {
      return;
    }
    const v = viewRef.current;
    const s = Math.max(MIN, Math.min(MAX, v.s + buoc));
    const k = s / v.s;
    const cx = vp.clientWidth / 2;
    const cy = vp.clientHeight / 2;
    datView({ s, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k });
  };

  if (boCuc.bands.length === 0) {
    return <div className="emptybox">Chưa bốc sơ đồ.</div>;
  }

  /* Đường nối tính thẳng từ toạ độ: không đo DOM nên không phụ thuộc lúc nào font tải
     xong, và không bị transform của khung ảnh hưởng. */
  const duong = nodes.flatMap((node) => {
    const a = boCuc.nodes[node.nodeId];
    if (!a) {
      return [];
    }
    const ve = (toId: string | null, slot: number | null, lop: string) => {
      if (!toId || !slot) {
        return null;
      }
      const b = boCuc.nodes[toId];
      if (!b) {
        return null;
      }
      const x1 = a.x + CARD_W;
      const y1 = a.y + CARD_H / 2;
      const x2 = b.x;
      // Ghế trên và ghế dưới của ô đích, theo đúng tỉ lệ thẻ ở CSS.
      const y2 = b.y + (slot === 1 ? CARD_H * 0.55 : CARD_H * 0.84);
      const mx = x2 > x1 ? x1 + (x2 - x1) / 2 : x1 + 16;

      return (
        <path
          key={`${node.nodeId}-${lop}`}
          className={`de-edge ${lop}`}
          d={`M${x1} ${y1} H${mx} V${y2} H${x2}`}
          fill="none"
        />
      );
    };

    return [ve(node.winToId, node.winToSlot, "is-win"), ve(node.loseToId, node.loseToSlot, "is-lose")].filter(
      Boolean
    );
  });

  return (
    <div className="de-board">
      <div
        className="de-vp"
        ref={vpRef}
        onPointerDown={xuong}
        onPointerMove={di}
        onPointerUp={len}
        onPointerCancel={len}
      >
        <div
          className="de-canvas"
          style={{
            width: boCuc.width,
            height: boCuc.height,
            transform: `translate(${view.x}px,${view.y}px) scale(${view.s})`,
          }}
        >
          <svg className="de-edges" width={boCuc.width} height={boCuc.height} aria-hidden="true">
            {duong}
          </svg>

          {boCuc.bands.map((band) => (
            <div key={band.key}>
              <div
                className={`de-band is-${band.key}`}
                style={{ left: band.x, top: band.y, width: band.w, height: band.h }}
              >
                <div className="de-band-hd">
                  {band.label}
                  <span>{band.sub}</span>
                </div>
              </div>
              {band.cols.map((col) => (
                <div key={col.name} className="de-colhd" style={{ left: col.x, top: col.y }}>
                  {col.name}
                </div>
              ))}
            </div>
          ))}

          {nodes.map((node) => {
            const p = boCuc.nodes[node.nodeId];
            if (!p) {
              return null;
            }
            const xong = node.status === "CONFIRMED";
            const coNguoi = node.players.some((x) => x.id);

            return (
              <div
                key={node.nodeId}
                className={`de-node ${coNguoi ? "" : "is-empty"} ${node.mine ? "is-mine" : ""}`}
                style={{ left: p.x, top: p.y, width: CARD_W }}
                data-node-id={node.nodeId}
              >
                <div className="de-code">{node.code}</div>
                {([1, 2] as const).map((slot) => {
                  const player = node.players[slot - 1];
                  const { text, mo } = seatName(node, slot, nhan[node.nodeId]);
                  const toi = !!myPlayerId && player?.id === myPlayerId;

                  return (
                    <div
                      key={slot}
                      className={`de-seat ${xong && player?.isWinner ? "is-won" : ""} ${toi ? "is-me" : ""}`}
                    >
                      <span className={`de-name ${mo ? "is-src" : ""}`}>
                        {text}
                        {node.nodeId === chungKetId && xong && player?.isWinner ? (
                          <span className="cupvd" role="img" aria-label={VO_DICH} title={VO_DICH}>
                            {" 🏆"}
                          </span>
                        ) : null}
                      </span>
                      <span className="de-score">{player?.score === null || player?.score === undefined ? "" : player.score}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div className="de-tools">
        {/* Nói rõ luật ngón tay: một ngón là CUỘN TRANG. Không nói thì người dùng
            vuốt trong sơ đồ, trang không nhúc nhích, và họ không biết phải làm gì -
            đúng cái bẫy mà touch-action: none dựng ra. */}
        <span className="tiny">Kéo để di chuyển · chụm 2 ngón để phóng to</span>
        <button type="button" className="de-zoom" onClick={() => phong(-0.15)} aria-label="Thu nhỏ">
          <Icon d={ICON_TRU} />
        </button>
        {oCuaToi ? (
          <button type="button" className="de-zoom" onClick={veTranCuaToi} aria-label="Về trận của tôi">
            <Icon d={ICON_TOI} chuY />
          </button>
        ) : null}
        <button type="button" className="de-zoom" onClick={vuaKhung} aria-label="Vừa khung">
          <Icon d={ICON_VUA} />
        </button>
        <button type="button" className="de-zoom" onClick={() => phong(0.15)} aria-label="Phóng to">
          <Icon d={ICON_CONG} />
        </button>
      </div>
    </div>
  );
}
